'use client';

import { useSession } from '@side/next-auth/react';
import { createContext, useMemo } from 'react';
import type { IdentityRequest } from '$api-clients/hooks';
import { useIdentity } from '$api-clients/hooks';

export const UserInfoContext = createContext<{
  userinfo?: IdentityRequest<'/auth/userinfo'>['Data'];
  isLoading: boolean;
  error?: Error;
}>({ isLoading: true, userinfo: undefined, error: undefined });

export function UserInfoProvider({
  userinfo,
  children,
}: {
  userinfo?: IdentityRequest<'/auth/userinfo'>['Data'];
  children: React.ReactNode;
}) {
  const session = useSession();
  // Load data client side if it wasn't passed in from the server (pages router) and user is authenticated
  const { data, isLoading, error } = useIdentity(
    '/auth/userinfo',
    !session?.data || userinfo ? null : undefined,
  );

  const value = useMemo(
    () => ({
      userinfo: userinfo ?? data,
      isLoading: userinfo ? false : session.status === 'loading' || isLoading,
      error,
    }),
    [userinfo, data, session.status, isLoading, error],
  );

  return (
    <UserInfoContext.Provider value={value}>
      {children}
    </UserInfoContext.Provider>
  );
}
